import { FormEvent, useState, useTransition } from "react";
import { toast } from "sonner";

import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import { LOGIN_SIGNUP_HINT } from "@buildpy/core/modules/onboarding/rules";
import type { SignInResult } from "@buildpy/core/modules/org/action-types";
import { Link, navigateTo } from "@/lib/nav";
import { settleRedirect } from "@/lib/redirect-error";
import { signIn } from "@/api/actions/org";
import { ONBOARDING_PATH, SIGNUP_VERIFY_PATH } from "@/modules/plan/copy";
import { signupHintOf } from "./login-form-state";

/**
 * 로그인 폼.
 *
 * 성공하면 액션이 `/app`(?next= 가 있으면 그 주소)으로 **던진다**. 이동은 rpc 클라이언트 onRedirect 가 이미 했으므로 RpcRedirect 는 조용히 끝낸다 —
 * 다시 던지면 오류 경계가 받아 콘솔에 오류가 찍히고, 「실패했습니다」 로 삼켜도 안 된다. 401 은 settleRedirect 한 벌(로그인 없는 화면이면 이동 없이 토스트 · /login 은 그 목록 밖이라 세션을 지우고 /login 으로 다시, 토스트 없음) · 그 밖의 라우터 신호(404)는 다시 던진다.
 */

const LoginForm = ({ next }: { next?: string }) => {
  const [pending, startTransition] = useTransition();
  const [hint, setHint] = useState(false);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    // 가드가 넘긴 원래 주소 — 서버 signIn 이 safeNextPath 로 다시 걸러 그리로 던진다.
    if (next) form.set("next", next);

    startTransition(async () => {
      try {
        const result: SignInResult = await signIn(form);
        // 셀프 가입하고 회사 정보를 아직 안 넣은 계정 — 세션은 섰으니 회사 정보 입력으로.
        if (result.needsOnboarding) {
          navigateTo(ONBOARDING_PATH);
          return;
        }
        setHint(signupHintOf(result));
        if (!result.ok) toast.error(result.message);
      } catch (caught) {
        if (settleRedirect(caught)) return;
        toast.error("들어가지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  return <LoginFormView hint={hint} onSubmit={onSubmit} pending={pending} />;
};

export const LoginFormView = ({
  hint,
  onSubmit,
  pending,
}: {
  hint: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  pending: boolean;
}) => {
  return (
    <form className="flex flex-col gap-y-4" onSubmit={onSubmit}>
      <div className="flex flex-col gap-y-1.5">
        <Label htmlFor="login-email">이메일</Label>
        <Input
          autoComplete="username"
          disabled={pending}
          id="login-email"
          inputMode="email"
          maxLength={200}
          name="email"
          placeholder="you@company.com"
          required
          type="email"
        />
      </div>

      <div className="flex flex-col gap-y-1.5">
        <Label htmlFor="login-password">비밀번호</Label>
        <Input
          autoComplete="current-password"
          disabled={pending}
          id="login-password"
          name="password"
          required
          type="password"
        />
      </div>

      {hint && (
        <p className="text-sm text-secondary-foreground" data-testid="login-signup-hint" role="status">
          {LOGIN_SIGNUP_HINT}{" "}
          <Link className="font-medium text-primary underline underline-offset-4 hover:text-foreground" href={SIGNUP_VERIFY_PATH}>
            다시 받기
          </Link>
        </p>
      )}

      <Button className="w-full" disabled={pending} type="submit">
        {pending ? "들어가는 중…" : "로그인"}
      </Button>
    </form>
  );
};

export default LoginForm;
